import React, { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import {
    getPasskeys,
    getPasskeyRegistrationOptions,
    verifyPasskeyRegistration,
} from "./api";
import passkeyIcon from "./assets/passkeymajor.svg";

const PASSKEY_REMINDER_DAYS = 30;

function reminderStorageKey(userId) {
    return `urban-portal-passkey-reminder:${userId}`;
}

function getReminderTimestamp(userId) {
    try {
        return Number(window.localStorage.getItem(reminderStorageKey(userId)) || 0);
    } catch {
        return 0;
    }
}

function setReminderTimestamp(userId, timestamp) {
    try {
        window.localStorage.setItem(reminderStorageKey(userId), String(timestamp));
    } catch {
        // The prompt can still be dismissed for this session when storage is unavailable.
    }
}

export default function PasskeyPrompt({ user }) {
    const [visible, setVisible] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (typeof document === "undefined" || !document.body) return undefined;

        const updateModalState = () => {
            setModalOpen(Boolean(document.querySelector(".modal-overlay")));
        };
        updateModalState();

        const observer = new MutationObserver(updateModalState);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function checkPasskeys() {
            if (
                !user?.id ||
                typeof window === "undefined" ||
                typeof window.PublicKeyCredential === "undefined"
            ) {
                return;
            }

            if (getReminderTimestamp(user.id) > Date.now()) return;

            try {
                const response = await getPasskeys();
                if (
                    !cancelled &&
                    response?.ok &&
                    Array.isArray(response.passkeys) &&
                    response.passkeys.length === 0
                ) {
                    setVisible(true);
                }
            } catch {
                // Passkey setup remains available in Settings if this check fails.
            }
        }

        checkPasskeys();
        return () => {
            cancelled = true;
        };
    }, [user?.id]);

    const remindLater = () => {
        const remindAt = Date.now() + PASSKEY_REMINDER_DAYS * 24 * 60 * 60 * 1000;
        setReminderTimestamp(user.id, remindAt);
        setVisible(false);
        setError("");
    };

    const setUpPasskey = async () => {
        setBusy(true);
        setError("");
        try {
            const optionsResponse = await getPasskeyRegistrationOptions();
            if (!optionsResponse?.ok || !optionsResponse.options) {
                throw new Error(optionsResponse?.message || "Failed to start passkey setup");
            }

            const suggestedName = optionsResponse.suggestedName || "New passkey";
            const name = window.prompt("Name this passkey", suggestedName);
            if (!name?.trim()) {
                setBusy(false);
                return;
            }

            const credential = await startRegistration({
                optionsJSON: optionsResponse.options,
            });
            const result = await verifyPasskeyRegistration(
                credential,
                optionsResponse.options.challenge,
                name.trim()
            );
            if (!result?.ok) {
                throw new Error(result?.message || "Failed to save passkey");
            }

            setVisible(false);
        } catch (err) {
            setError(
                err?.name === "NotAllowedError"
                    ? "Passkey setup was cancelled. You can try again or be reminded later."
                    : err?.message || "Failed to set up passkey"
            );
        } finally {
            setBusy(false);
        }
    };

    if (!visible || modalOpen) return null;

    return (
        <div className="beta-passkey-prompt__overlay" role="presentation">
            <section
                className="beta-passkey-prompt"
                role="dialog"
                aria-modal="true"
                aria-labelledby="beta-passkey-prompt-title"
                aria-describedby="beta-passkey-prompt-description"
            >
                <div className="beta-passkey-prompt__icon" aria-hidden="true">
                    <img src={passkeyIcon} alt="" />
                </div>
                <h2 id="beta-passkey-prompt-title">Set up a passkey</h2>
                <p id="beta-passkey-prompt-description">
                    Sign in faster with your face, fingerprint or PIN. No apps,
                    passwords or codes needed.
                </p>
                {error ? <div className="beta-passkey-prompt__error">{error}</div> : null}
                <div className="beta-passkey-prompt__actions">
                    <button
                        type="button"
                        className="beta-passkey-prompt__primary"
                        onClick={setUpPasskey}
                        disabled={busy}
                    >
                        {busy ? "Setting up…" : "Set up a passkey"}
                    </button>
                    <button
                        type="button"
                        className="beta-passkey-prompt__secondary"
                        onClick={remindLater}
                        disabled={busy}
                    >
                        Remind me in 30 days
                    </button>
                </div>
            </section>
        </div>
    );
}
